import { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react'
import { useIntl } from 'react-intl'
import styled, { createGlobalStyle, keyframes } from 'styled-components'
import { rem } from 'polished'
import BodyText from 'LuxKit/Typography/BodyText'
import { stripContextTags } from 'tripPlanner/utils/contextTag'
import type { MessageItem } from 'tripPlanner/components/Chat/MessagesList'

const MIN_TIMELINE_TURNS = 12
const MIN_TIMELINE_HEIGHT_PX = 96
const MAX_VISIBLE_MARKERS = 56
const RECENT_VISIBLE_MARKER_RATIO = 0.35
const MIN_RECENT_VISIBLE_MARKERS = 18
const MARKER_GAP_PX = 7
const MIN_MARKER_HEIGHT_PX = 2
const MAX_MARKER_HEIGHT_PX = 3
const FLASH_CLASS_NAME = 'assistant-message-timeline-flash'
const FLASH_DURATION_MS = 900
const JUMP_TARGET_OFFSET_RATIO = 0.25

interface TimelineEntry {
  targetMessageId: string
  userPreview: string
  assistantPreview?: string
}

interface VisibleTimelineEntry {
  key: string
  entry: TimelineEntry
}

interface Props {
  messages: Array<MessageItem>
  messagesWrapperRef: MutableRefObject<HTMLDivElement | null>
}

interface MarkerStyleProps {
  $heightPx: number
  $isActive: boolean
  $widthPx: number
}

interface MarkerRailStyleProps {
  $heightPx: number
}

interface TimelineEntryButtonProps {
  item: VisibleTimelineEntry
  visibleIndex: number
  activeVisibleStartIndex: number
  hoveredVisibleIndex: number | undefined
  markerHeight: number
  onHover: (visibleIndex: number) => void
  onJump: (entry: TimelineEntry) => void
}

type TextMessagePart = Extract<MessageItem['parts'][number], { type: 'text' }>

const flashMessage = keyframes`
  0% {
    box-shadow: inset 0 0 0 999px rgba(80, 80, 80, 0);
  }

  30% {
    box-shadow: inset 0 0 0 999px rgba(80, 80, 80, 0.16);
  }

  100% {
    box-shadow: inset 0 0 0 999px rgba(80, 80, 80, 0);
  }
`

const TimelineGlobalStyle = createGlobalStyle`
  [data-message-id].${FLASH_CLASS_NAME} [data-message-bubble] {
    animation: ${flashMessage} ${FLASH_DURATION_MS}ms ease-out;
    border-radius: ${rem(12)};
  }
`

const TimelineShell = styled.nav`
  position: sticky;
  top: 50%;
  z-index: 4;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: ${rem(44)};
  height: 0;
  margin-left: ${rem(-28)};
  transform: translateY(-50%);
  pointer-events: none;
`

const MarkerRail = styled.div<MarkerRailStyleProps>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${rem(MARKER_GAP_PX)};
  width: 100%;
  height: ${props => rem(props.$heightPx)};
  pointer-events: auto;
`

const MarkerSlot = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  min-width: ${rem(36)};
`

const MarkerButton = styled.button<MarkerStyleProps>`
  width: ${props => rem(props.$widthPx)};
  height: ${props => rem(props.$heightPx)};
  padding: 0;
  border: 0;
  border-radius: ${rem(8)};
  background: ${props => {
    if (props.$isActive) return props.theme.palette.neutral.surface.lightForegroundAlt
    return props.theme.palette.neutral.surface.lightForeground
  }};
  opacity: ${props => props.$isActive ? 0.72 : 0.58};
  cursor: pointer;
  transition:
    width 180ms ease,
    height 180ms ease,
    background-color 180ms ease,
    opacity 180ms ease;

  &:hover,
  &:focus-visible {
    opacity: 1;
    outline: none;
  }
`

const PreviewBubble = styled.div`
  position: absolute;
  top: 50%;
  left: ${rem(44)};
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  width: ${rem(320)};
  max-width: calc(100vw - ${rem(96)});
  padding: ${rem(10)} ${rem(12)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${rem(12)};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  box-shadow: ${props => props.theme.shadow.flat.small};
  transform: translateY(-50%);
  pointer-events: auto;
  cursor: pointer;
`

const PreviewText = styled(BodyText)`
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
`

const UserPreviewText = styled(PreviewText)`
  -webkit-line-clamp: 2;
`

const AssistantPreviewText = styled(PreviewText)`
  -webkit-line-clamp: 3;
`

function isPreviewTextPart(message: MessageItem, part: MessageItem['parts'][number]): part is TextMessagePart {
  return part.type === 'text' &&
    (message.role !== 'assistant' || part.assistantTextRole !== 'interim')
}

function stripPreviewMarkdown(text: string): string {
  return text
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/[`*_~>#]/g, '')
}

function getMessageText(message: MessageItem | undefined): string {
  if (!message) return ''

  const text = message.parts
    .filter((part): part is TextMessagePart => isPreviewTextPart(message, part))
    .map(part => part.content)
    .join('\n')
    .trim()
  const previewText = text || message.rawContent?.trim() || ''

  return stripContextTags(previewText)
    .replace(/^#+assistant_text_boundary.*$/gim, '')
    .split('\n')
    .map(stripPreviewMarkdown)
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

function buildTimelineEntries(messages: Array<MessageItem>): Array<TimelineEntry> {
  return messages.reduce<Array<TimelineEntry>>((entries, message, index) => {
    if (message.role !== 'user' || message.isPlaceholder) {
      return entries
    }

    const userPreview = getMessageText(message)
    if (!userPreview) {
      return entries
    }

    const nextUserIndex = messages.findIndex((candidate, candidateIndex) => (
      candidateIndex > index && candidate.role === 'user'
    ))
    const searchEndIndex = nextUserIndex === -1 ? messages.length : nextUserIndex
    const assistantMessage = messages
      .slice(index + 1, searchEndIndex)
      .find(candidate => candidate.role === 'assistant' && !candidate.isPlaceholder)
    const assistantPreview = getMessageText(assistantMessage)

    entries.push({
      targetMessageId: message.id,
      userPreview,
      ...(assistantPreview ? { assistantPreview } : {}),
    })
    return entries
  }, [])
}

function getTimelineHeight(entriesLength: number): number {
  return Math.max(MIN_TIMELINE_HEIGHT_PX, entriesLength * (MAX_MARKER_HEIGHT_PX + MARKER_GAP_PX))
}

function getMarkerHeight(entriesLength: number, timelineHeight: number): number {
  const totalGapHeight = Math.max(0, entriesLength - 1) * MARKER_GAP_PX
  const availableHeight = Math.max(entriesLength * MIN_MARKER_HEIGHT_PX, timelineHeight - totalGapHeight)
  return Math.max(
    MIN_MARKER_HEIGHT_PX,
    Math.min(MAX_MARKER_HEIGHT_PX, availableHeight / entriesLength),
  )
}

function getMarkerWidth(index: number, hoveredVisibleIndex: number | undefined): number {
  if (hoveredVisibleIndex !== undefined) {
    const hoverDistance = Math.abs(index - hoveredVisibleIndex)
    if (hoverDistance === 0) return 24
    if (hoverDistance === 1) return 16
    if (hoverDistance === 2) return 10
  }

  return 8
}

function getMessageSelector(messageId: string): string {
  const escapedMessageId = typeof CSS !== 'undefined' && CSS.escape
    ? CSS.escape(messageId)
    : messageId.replace(/["\\]/g, '\\$&')

  return `[data-message-id="${escapedMessageId}"]`
}

function TimelineEntryButton({
  item,
  visibleIndex,
  activeVisibleStartIndex,
  hoveredVisibleIndex,
  markerHeight,
  onHover,
  onJump,
}: TimelineEntryButtonProps) {
  const intl = useIntl()
  const isActive = hoveredVisibleIndex === undefined
    ? visibleIndex >= activeVisibleStartIndex && visibleIndex <= activeVisibleStartIndex + 2
    : visibleIndex === hoveredVisibleIndex
  const markerWidth = getMarkerWidth(visibleIndex, hoveredVisibleIndex)
  const showPreview = hoveredVisibleIndex === visibleIndex

  const handleMouseEnter = useCallback(() => {
    onHover(visibleIndex)
  }, [onHover, visibleIndex])

  const handleFocus = useCallback(() => {
    onHover(visibleIndex)
  }, [onHover, visibleIndex])

  const handleClick = useCallback(() => {
    onJump(item.entry)
  }, [item.entry, onJump])

  return (
    <MarkerSlot onMouseEnter={handleMouseEnter} onFocus={handleFocus}>
      <MarkerButton
        type="button"
        aria-label={intl.formatMessage({
          id: 'eKRA4MiUxasWoUGC8zwG',
          defaultMessage: 'Jump to message: {preview}',
          description: 'Accessible label for jumping to a message from the assistant chat timeline',
        }, { preview: item.entry.userPreview })}
        $heightPx={markerHeight}
        $isActive={isActive}
        $widthPx={markerWidth}
        onClick={handleClick}
      />
      {showPreview && (
        <PreviewBubble
          onClick={handleClick}
        >
          <UserPreviewText variant="medium" weight="bold">{item.entry.userPreview}</UserPreviewText>
          {item.entry.assistantPreview && (
            <AssistantPreviewText variant="medium" colour="neutral-three">
              {item.entry.assistantPreview}
            </AssistantPreviewText>
          )}
        </PreviewBubble>
      )}
    </MarkerSlot>
  )
}

function findActiveVisibleEntryIndex(
  visibleEntries: Array<VisibleTimelineEntry>,
  scrollContainer: HTMLDivElement,
): number {
  const containerRect = scrollContainer.getBoundingClientRect()
  const targetY = containerRect.top + containerRect.height * 0.35
  let bestIndex = 0
  let bestDistance = Number.POSITIVE_INFINITY

  visibleEntries.forEach((item, index) => {
    const currentElement = scrollContainer.querySelector<HTMLElement>(getMessageSelector(item.entry.targetMessageId))
    if (!currentElement) {
      return
    }

    const currentDistance = Math.abs(currentElement.getBoundingClientRect().top - targetY)
    if (currentDistance < bestDistance) {
      bestIndex = index
      bestDistance = currentDistance
    }
  })

  return bestIndex
}

function getVisibleTimelineEntries(
  entries: Array<TimelineEntry>,
): Array<VisibleTimelineEntry> {
  if (entries.length <= MAX_VISIBLE_MARKERS) {
    return entries.map(entry => ({
      key: entry.targetMessageId,
      entry,
    }))
  }

  const recentCount = Math.min(
    entries.length,
    MAX_VISIBLE_MARKERS,
    Math.max(
      MIN_RECENT_VISIBLE_MARKERS,
      Math.floor(MAX_VISIBLE_MARKERS * RECENT_VISIBLE_MARKER_RATIO),
    ),
  )
  const recentStartIndex = entries.length - recentCount
  const olderVisibleCount = Math.max(0, MAX_VISIBLE_MARKERS - recentCount)
  const olderItems = Array.from({ length: olderVisibleCount }, (_, visibleIndex) => {
    const sourceIndex = Math.floor((visibleIndex * recentStartIndex) / olderVisibleCount)
    const entry = entries[sourceIndex]

    return {
      key: entry.targetMessageId,
      entry,
    }
  })

  const recentItems = entries.slice(recentStartIndex).map(entry => ({
    key: entry.targetMessageId,
    entry,
  }))

  return [...olderItems, ...recentItems]
}

function getActiveVisibleStartIndex(activeVisibleIndex: number, visibleEntryCount: number): number {
  return Math.max(0, Math.min(activeVisibleIndex, visibleEntryCount - 3))
}

export default function AssistantMessageTimeline({
  messages,
  messagesWrapperRef,
}: Props) {
  const intl = useIntl()
  const entries = useMemo(() => buildTimelineEntries(messages), [messages])
  const [activeVisibleIndex, setActiveVisibleIndex] = useState(0)
  const [hoveredVisibleIndex, setHoveredVisibleIndex] = useState<number | undefined>(undefined)
  const frameRef = useRef<number | undefined>(undefined)
  const flashTimeoutRef = useRef<number | undefined>(undefined)

  const visibleEntries = useMemo(() => (
    getVisibleTimelineEntries(entries)
  ), [entries])
  const activeVisibleStartIndex = useMemo(() => (
    getActiveVisibleStartIndex(activeVisibleIndex, visibleEntries.length)
  ), [activeVisibleIndex, visibleEntries.length])
  const timelineHeight = useMemo(() => getTimelineHeight(visibleEntries.length), [visibleEntries.length])
  const markerHeight = useMemo(() => getMarkerHeight(visibleEntries.length, timelineHeight), [visibleEntries.length, timelineHeight])

  const syncActiveEntry = useCallback(() => {
    const scrollContainer = messagesWrapperRef.current
    if (!scrollContainer || visibleEntries.length === 0) {
      setActiveVisibleIndex(0)
      return
    }

    setActiveVisibleIndex(findActiveVisibleEntryIndex(visibleEntries, scrollContainer))
  }, [messagesWrapperRef, visibleEntries])

  const scheduleActiveEntrySync = useCallback(() => {
    if (frameRef.current !== undefined) {
      window.cancelAnimationFrame(frameRef.current)
    }

    frameRef.current = window.requestAnimationFrame(() => {
      frameRef.current = undefined
      syncActiveEntry()
    })
  }, [syncActiveEntry])

  useEffect(() => {
    const scrollContainer = messagesWrapperRef.current
    if (!scrollContainer || entries.length < MIN_TIMELINE_TURNS) {
      return undefined
    }

    syncActiveEntry()
    scrollContainer.addEventListener('scroll', scheduleActiveEntrySync, { passive: true })

    return () => {
      scrollContainer.removeEventListener('scroll', scheduleActiveEntrySync)
      if (frameRef.current !== undefined) {
        window.cancelAnimationFrame(frameRef.current)
        frameRef.current = undefined
      }
    }
  }, [entries.length, messagesWrapperRef, scheduleActiveEntrySync, syncActiveEntry])

  useEffect(() => {
    return () => {
      if (flashTimeoutRef.current !== undefined) {
        window.clearTimeout(flashTimeoutRef.current)
      }
    }
  }, [])

  const handleJump = useCallback((entry: TimelineEntry) => {
    const scrollContainer = messagesWrapperRef.current
    const targetElement = scrollContainer?.querySelector<HTMLElement>(getMessageSelector(entry.targetMessageId))
    if (!scrollContainer || !targetElement) {
      return
    }

    const containerRect = scrollContainer.getBoundingClientRect()
    const targetRect = targetElement.getBoundingClientRect()
    scrollContainer.scrollTop = Math.max(
      0,
      scrollContainer.scrollTop + targetRect.top - containerRect.top - containerRect.height * JUMP_TARGET_OFFSET_RATIO,
    )
    targetElement.classList.remove(FLASH_CLASS_NAME)
    window.requestAnimationFrame(() => {
      targetElement.classList.add(FLASH_CLASS_NAME)
    })

    if (flashTimeoutRef.current !== undefined) {
      window.clearTimeout(flashTimeoutRef.current)
    }
    flashTimeoutRef.current = window.setTimeout(() => {
      targetElement.classList.remove(FLASH_CLASS_NAME)
      flashTimeoutRef.current = undefined
    }, FLASH_DURATION_MS)
    syncActiveEntry()
  }, [messagesWrapperRef, syncActiveEntry])

  const handleHover = useCallback((visibleIndex: number) => {
    setHoveredVisibleIndex(visibleIndex)
  }, [])

  const handleMouseLeave = useCallback(() => {
    setHoveredVisibleIndex(undefined)
  }, [])

  if (entries.length < MIN_TIMELINE_TURNS) {
    return null
  }

  const timelineLabel = intl.formatMessage({
    id: 'GvE2LJ4p87DQQsDN0KSi',
    defaultMessage: 'Message timeline',
    description: 'Accessible label for the assistant chat message timeline navigation',
  })

  return (
    <>
      <TimelineGlobalStyle />
      <TimelineShell aria-label={timelineLabel}>
        <MarkerRail $heightPx={timelineHeight} onMouseLeave={handleMouseLeave}>
          {visibleEntries.map((item, index) => (
            <TimelineEntryButton
              key={item.key}
              item={item}
              visibleIndex={index}
              activeVisibleStartIndex={activeVisibleStartIndex}
              hoveredVisibleIndex={hoveredVisibleIndex}
              markerHeight={markerHeight}
              onHover={handleHover}
              onJump={handleJump}
            />
          ))}
        </MarkerRail>
      </TimelineShell>
    </>
  )
}
